import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { UserCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useCustomerDetails } from "@api/customersController";
import { useRefreshCustomerSummary } from "@api/intelligenceController";
import { useMerchant } from "@auth";
import { useRealtimeChannel } from "../../../realtime/useRealtimeChannel";
import { HINT_COALESCE_MS, useCoalescedInvalidation } from "../../../realtime/useCoalescedInvalidation";
import { channels } from "../../../realtime/channels";
import SummaryCard from "./SummaryCard";
import AgedDebtCard from "./AgedDebtCard";
import ActivityFeed from "./ActivityFeed";
import { PayerTypeLabel, WatchFlagChips } from "@areas/trade-accounts/components/FactCells";
import Tooltip from "@components/Tooltip";

// The newest server-written activity timestamp in the cached feed, ignoring optimistic rows —
// the reconcile waits for this to advance past its baseline before regenerating the read.
function newestServerActivityAt(data) {
  const items = data?.pages?.flatMap((page) => page?.items ?? []) ?? [];
  let newest = 0;
  for (const item of items) {
    if (typeof item.id === "string" && item.id.startsWith("optimistic-")) continue;
    const at = item.occurredAt ? new Date(item.occurredAt).getTime() : 0;
    if (at > newest) newest = at;
  }
  return newest;
}

const CustomerContextPanel = forwardRef(function CustomerContextPanel(
  { customer, onClose, onViewTradeAccount, onReadyChange, onPreviewStep, onOpenPaymentRequest },
  ref,
) {
  const { data: detail, isLoading } = useCustomerDetails(customer.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const showCreditCode = merchant?.erpSettings?.showCreditCode;
  const refreshCustomerSummary = useRefreshCustomerSummary();
  const summaryRefreshTimer = useRef(null);
  const lastSummaryRefreshAt = useRef(0);
  // The panel's scroll viewport — the activity feed observes a sentinel against it for infinite scroll.
  const scrollRef = useRef(null);

  // Regenerating the summary is an LLM call. A lone hint regenerates at once; a burst regenerates
  // once at its start and once after it has been quiet for SUMMARY_QUIET_MS.
  // Driven by the post-write ActivityCreated hint rather than each action's success handler, so
  // the regeneration reads the written row instead of racing it.
  const SUMMARY_QUIET_MS = HINT_COALESCE_MS;
  const refreshSummary = useCallback(() => {
    if (!customer?.id) return;

    const regenerate = () => {
      lastSummaryRefreshAt.current = Date.now();
      refreshCustomerSummary(customer.id);
    };

    const idle =
      summaryRefreshTimer.current === null &&
      Date.now() - lastSummaryRefreshAt.current >= SUMMARY_QUIET_MS;
    if (idle) {
      regenerate();
      return;
    }

    clearTimeout(summaryRefreshTimer.current);
    summaryRefreshTimer.current = setTimeout(() => {
      summaryRefreshTimer.current = null;
      regenerate();
    }, SUMMARY_QUIET_MS);
  }, [customer?.id, refreshCustomerSummary]);

  // The customer timeline renders every event name, so hints are not filtered here.
  const customerChannel =
    merchant?.id && customer?.id ? channels.customer(merchant.id, customer.id) : null;
  useCoalescedInvalidation(customerChannel, ["customerActivities", customer.id]);
  useRealtimeChannel(customerChannel, () => refreshSummary());

  // Fallback for a missed realtime hint (the AppSync socket connects lazily and has no replay,
  // so a hint fired before it's live is lost): after a write the modal can't see reflected yet,
  // poll the feed with backoff until a new server-written row lands, then run the same throttled
  // read regeneration the hint would have. Bounded (~45s, covering the action-service outbox
  // drain) and early-exits once the row appears; optimistic rows are ignored so we only react to
  // the real one, which is also why this never races the write.
  const reconcileCancel = useRef(null);
  const reconcileActivity = useCallback(() => {
    if (!customer?.id) return;
    reconcileCancel.current?.();
    const queryKey = ["customerActivities", customer.id];
    const baseline = newestServerActivityAt(queryClient.getQueryData(queryKey));
    const delays = [3000, 3000, 6000, 12000, 21000];
    const timers = [];
    let stopped = false;
    const attempt = (i) => {
      if (stopped || i >= delays.length) return;
      const timer = setTimeout(async () => {
        if (stopped) return;
        try {
          await queryClient.refetchQueries({ queryKey });
        } catch {
          /* a failed poll just falls through to the next attempt */
        }
        if (stopped) return;
        if (newestServerActivityAt(queryClient.getQueryData(queryKey)) > baseline) {
          stopped = true;
          refreshSummary();
          return;
        }
        attempt(i + 1);
      }, delays[i]);
      timers.push(timer);
    };
    attempt(0);
    reconcileCancel.current = () => {
      stopped = true;
      timers.forEach(clearTimeout);
    };
  }, [customer?.id, queryClient, refreshSummary]);

  useImperativeHandle(ref, () => ({ reconcileActivity }), [reconcileActivity]);

  // Abandon an in-flight reconcile on unmount or when the customer changes, so it can't
  // regenerate the wrong customer's read.
  useEffect(() => () => reconcileCancel.current?.(), [customer?.id]);
  useEffect(() => () => clearTimeout(summaryRefreshTimer.current), []);

  const accountCode = detail?.accountCode ?? customer.accountCode;
  const email = detail?.email ?? customer.email;
  const subline = [accountCode, email].filter(Boolean).join(" · ");

  const viewTradeAccount = () => {
    navigate(`/accounts?account=${customer.id}`);
    onViewTradeAccount?.();
  };

  return (
    <div className="flex max-h-[80vh] w-[600px] flex-col overflow-hidden rounded-2xl bg-zinc-50 text-left shadow-xl">
      <div className="flex items-start gap-3 border-b border-zinc-200 p-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap- -mt-1">
            <h3 className="min-w-0 flex-1 truncate text-xl font-semibold text-zinc-900">
              {detail?.name ?? customer.name}
            </h3>
            {onViewTradeAccount && (
              <button
                type="button"
                onClick={viewTradeAccount}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-medium text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 active:bg-zinc-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                <UserCircleIcon aria-hidden="true" className="size-5 text-zinc-500" />
                Account details
              </button>
            )}
          </div>
          {subline && (
            <p className="mt-0.5 truncate text-sm text-zinc-500">{subline}</p>
          )}
          {showCreditCode && detail?.creditCode && (
            <Tooltip content="Credit code" wrapperClassName="inline-flex">
              <span className="mt-1.5 -mb-4 inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-md border border-zinc-200 bg-zinc-50/25 px-2 py-0.5 text-xs font-medium text-zinc-700">
                {detail.creditCode}
              </span>
            </Tooltip>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md text-zinc-400 hover:text-zinc-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          <span className="sr-only">Close</span>
          <XMarkIcon aria-hidden="true" className="size-6" />
        </button>
      </div>

      <div ref={scrollRef} className="flex flex-col gap-4 overflow-y-auto p-5">
        {(detail?.activityState || detail?.timingPersona || detail?.watchFlags?.length > 0) && (
          <div className="flex flex-wrap items-center gap-2">
            {(detail?.activityState || detail?.timingPersona) && (
              <PayerTypeLabel
                activityState={detail.activityState}
                timingPersona={detail.timingPersona}
                lateBand={detail.lateBand}
                detail={detail.payerTypeDetail}
              />
            )}
            {detail?.watchFlags?.length > 0 && (
              <WatchFlagChips flags={detail.watchFlags} details={detail.watchFlagDetails} />
            )}
          </div>
        )}
        <SummaryCard customerId={customer.id} onReadyChange={onReadyChange} />
        <AgedDebtCard customer={detail} isLoading={isLoading} />
        <ActivityFeed
          customerId={customer.id}
          scrollContainerRef={scrollRef}
          onPreviewStep={onPreviewStep}
          onOpenPaymentRequest={onOpenPaymentRequest}
        />
      </div>
    </div>
  );
});

export default CustomerContextPanel;
